import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';
import { c as classNames } from './classnames-ba9ba5b9.js';
import { a as asyncRequestAnimationFrame } from './raf-4d880dcc.js';
import { g as getMaxZIndex } from './max-z-index-7a248b89.js';

// adapted from https://gist.github.com/davej/44e3bbec414ed4665220
async function transitionToPromise(el, property, value) {
  return new Promise((resolve) => {
    // ensure there is a transition on this property
    const { transitionProperty } = getComputedStyle(el);
    const properties = transitionProperty.split(', ');
    // if no transition on this property, set it and resolve
    if (!properties.includes(property)) {
      el.style[property] = value;
      resolve();
      return;
    }
    // otherwise, set up a listener for transitionend
    const transitionEnded = (e) => {
      if (e.propertyName !== property)
        return;
      el.removeEventListener('transitionend', transitionEnded);
      resolve();
    };
    el.addEventListener('transitionend', transitionEnded);
    el.style[property] = value;
  });
}

class DragAndDrop {
  // Init the class with the element to be dragged
  constructor(el) {
    this.el = el;
  }
  // Start a drag movement on the element:
  // - adds a .market-drag-placeholder class to the dragged element
  // - clones the dragged element with a class of .market-drag-clone
  // - fires marketDragStart custom event on the dragged element
  start(coords) {
    this.canceled = false;
    this.startCoords = coords;
    const { el } = this;
    // fire start event
    const defaultPrevented = !el.dispatchEvent(new CustomEvent('marketDragStart', {
      bubbles: true,
      cancelable: true,
      detail: coords,
    }));
    if (defaultPrevented) {
      this.canceled = true;
      return;
    }
    // measure the dragged element
    const { width } = getComputedStyle(el);
    const { top, left } = el.getBoundingClientRect();
    // clone & append a dummy el to visually track the drag move
    const clone = el.cloneNode(true);
    clone.classList.add('market-drag-clone');
    Object.assign(clone.style, {
      width,
      top: `${top}px`,
      left: `${left}px`,
      zIndex: `${getMaxZIndex(document.body) + 1}`,
    });
    document.body.append(clone);
    this.clone = clone;
    // class to style placeholder while drag is in progress
    el.classList.add('market-drag-placeholder');
  }
  // Continues a drag movement on the element:
  // - visually tracks the drag move with the cloned element via CSS transform
  // - fires marketDragEnter & marketDragLeave custom events on drag targets
  move(coords) {
    if (this.canceled)
      return;
    const { el, clone, target, startCoords } = this;
    const { x, y } = coords;
    // get the first drag target that is not the clone
    const newTarget = [...document.elementsFromPoint(x, y)].find((target) => target !== clone);
    // if drag target is new
    if (newTarget !== target) {
      // fire leave on current target
      if (target) {
        target.dispatchEvent(new CustomEvent('marketDragLeave', {
          bubbles: true,
          detail: { el, x, y },
        }));
      }
      // fire enter on new target
      newTarget.dispatchEvent(new CustomEvent('marketDragEnter', {
        bubbles: true,
        detail: { el, x, y },
      }));
      // update current drag target
      this.target = newTarget;
    }
    // move the cloned el with the drag move
    const deltaX = x - startCoords.x;
    const deltaY = y - startCoords.y;
    Object.assign(clone.style, {
      transform: `translate(${deltaX}px, ${deltaY}px)`,
    });
  }
  // Ends a drag movement on the element:
  // - fires marketDragEnd custom event on the dragged element
  // - fires marketDragDrop custom event on drag target
  // - removes the cloned element after transitioning it back to the placeholder
  // - removes the .market-drag-placeholder class from the dragged element
  async end(coords) {
    if (this.canceled)
      return;
    const { el, clone } = this;
    const { x, y } = coords;
    // get the first drag target that is not the clone
    const dragTarget = [...document.elementsFromPoint(x, y)].find((target) => target !== clone);
    // fire end event on dragged element
    const defaultPrevented = !el.dispatchEvent(new CustomEvent('marketDragEnd', {
      bubbles: true,
      cancelable: true,
      detail: { dragTarget, x, y },
    }));
    // fire drop event on target
    if (!defaultPrevented) {
      dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.dispatchEvent(new CustomEvent('marketDragDrop', {
        bubbles: true,
        detail: { el, x, y },
      }));
    }
    // measure the elements once again
    const { width } = getComputedStyle(el);
    const elRect = el.getBoundingClientRect();
    const cloneRect = clone.getBoundingClientRect();
    const deltaX = cloneRect.left - elRect.left;
    const deltaY = cloneRect.top - elRect.top;
    // update the clone position so that its top & left origin
    // is the same as the dragged element (which may have been moved!)
    // delta transform will result in the clone appearing in the same spot
    Object.assign(clone.style, {
      width,
      top: `${elRect.top}px`,
      left: `${elRect.left}px`,
      transform: `translate(${deltaX}px, ${deltaY}px)`,
    });
    // remove the transform to transition clone back to the placeholder.
    if (deltaX || deltaY) {
      await asyncRequestAnimationFrame();
      clone.classList.add('market-drag-released');
      await transitionToPromise(clone, 'transform', 'translate(0, 0)');
    }
    // cleanup
    el.classList.remove('market-drag-placeholder');
    clone.remove();
  }
  // clean up var refs
  destroy() {
    this.el = null;
    this.clone = null;
    this.target = null;
    this.startCoords = null;
    this.canceled = null;
  }
}

const marketRowCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{--transition-duration:0.2s;--row-horizontal-padding:12px;--row-horizontal-spacing:16px;position:relative;outline:none;position:relative;display:block;margin:0 calc(-1 * var(--row-background-horizontal-outset-padding));background-color:var(--row-normal-state-background-color);transition:background-color var(--transition-duration)}@media (hover: hover){:host{}:host([role=\"option\"]:not([disabled]):hover){background-color:var(--row-hover-state-background-color)}}:host([size='small']){min-height:var(--row-small-size-min-height, 40px);font-size:var(--row-small-size-text-size);line-height:var(--row-small-size-text-leading);letter-spacing:var(--row-small-size-text-tracking)}:host([size='medium']){min-height:var(--row-medium-size-min-height, 56px);font-size:var(--row-medium-size-text-size);line-height:var(--row-medium-size-text-leading);letter-spacing:var(--row-medium-size-text-tracking)}:host([role=\"option\"]){border-radius:calc(var(--row-focus-ring-radius) * 1.5);cursor:pointer}:host([role=\"option\"][selected]:not([disabled],.has-slotted-control)){background-color:var(--row-selected-state-background-color)}:host([role=\"option\"]:not([disabled]):active),:host([role=\"option\"][selected]:not([disabled]):active){background-color:var(--row-pressed-state-background-color)}:host([role=\"option\"]:focus){outline:none;}:host([role=\"option\"][selected]:not([disabled],.has-slotted-control):focus){background-color:var(--row-focus-state-background-color)}:host([disabled]){background-color:transparent;color:var(--row-disabled-state-text-color);pointer-events:none}:host([disabled]) svg{fill:var(--row-disabled-state-drill-color)}:host::before{content:'';position:absolute;right:var(--row-background-horizontal-outset-padding);bottom:0;left:var(--row-background-horizontal-outset-padding);display:block;height:var(--row-separator-height);background-color:var(--row-normal-state-separator-color)}:host([role=\"option\"]:not([disabled]):hover)::before,:host([role=\"option\"]:not([disabled]):active)::before,:host(:last-of-type)::before{content:none}:host::after{--focus-ring-position:calc(-1 * (var(--row-focus-ring-border-size) + var(--row-focus-ring-buffer-size) + var(--row-focus-ring-border-size)));content:'';position:absolute;top:var(--focus-ring-position);right:var(--focus-ring-position);bottom:var(--focus-ring-position);left:var(--focus-ring-position);display:block;border:var(--row-focus-ring-border-size) solid var(--row-focus-ring-color);border-radius:calc(var(--row-focus-ring-radius) + var(--row-focus-ring-buffer-size) * 2);opacity:0;pointer-events:none;transition:opacity 0.2s;content:'';position:absolute;top:0;right:0;bottom:0;left:0;display:block}:host(.focus-visible)::after{opacity:0.5}.container{display:flex;justify-content:space-between;align-items:center;outline:none;text-decoration:none;-moz-column-gap:var(--row-horizontal-spacing);column-gap:var(--row-horizontal-spacing)}:host([size='small']) .container{padding:var(--row-small-size-vertical-padding) var(--row-horizontal-padding)}:host([size='medium']) .container{padding:var(--row-medium-size-vertical-padding) var(--row-horizontal-padding)}.main{display:flex;flex-grow:2;flex-direction:column;min-width:0}:host([size='small']) .main{gap:var(--row-small-size-text-vertical-spacing)}:host([size='medium']) .main{gap:var(--row-medium-size-text-vertical-spacing)}.side{display:flex;flex-direction:column;text-align:right}:host([size='small']) .side{gap:var(--row-small-size-text-vertical-spacing)}:host([size='medium']) .side{gap:var(--row-medium-size-text-vertical-spacing)}.hidden{display:none !important;}::slotted(*){cursor:inherit}::slotted([slot='label']){display:block;color:var(--row-normal-state-text-color)}@media (hover: hover){::slotted([slot='label']){}:host([role='option']:hover:not([disabled])) ::slotted([slot='label']){color:var(--row-hover-state-text-color)}}:host([role='option'][selected]:not([disabled],.has-slotted-control)) ::slotted([slot='label']){color:var(--row-selected-state-text-color)}:host([role='option']:not([disabled]):active) ::slotted([slot='label']){color:var(--row-pressed-state-text-color)}:host([role='option']:focus) ::slotted([slot='label']){color:var(--row-focus-state-text-color)}:host([disabled]) ::slotted([slot='label']){color:var(--row-disabled-state-text-color)}:host([size='small']) ::slotted([slot='label']){font-weight:var(--row-small-size-text-weight);font-size:var(--row-small-size-text-size);line-height:var(--row-small-size-text-leading);letter-spacing:var(--row-small-size-text-tracking)}:host([size='medium']) ::slotted([slot='label']){font-weight:var(--row-medium-size-text-weight);font-size:var(--row-medium-size-text-size);line-height:var(--row-medium-size-text-leading);letter-spacing:var(--row-medium-size-text-tracking)}::slotted([slot='subtext']){margin:0;color:var(--row-normal-state-subtext-color)}@media (hover: hover){::slotted([slot='subtext']){}:host([role='option']:hover:not([disabled])) ::slotted([slot='subtext']){color:var(--row-hover-state-subtext-color)}}:host([role='option'][selected]:not([disabled],.has-slotted-control)) ::slotted([slot='subtext']){color:var(--row-selected-state-subtext-color)}:host([role='option']:not([disabled]):active) ::slotted([slot='subtext']){color:var(--row-pressed-state-subtext-color)}:host([role='option']:focus) ::slotted([slot='subtext']){color:var(--row-focus-state-subtext-color)}:host([disabled]) ::slotted([slot='subtext']){color:var(--row-disabled-state-subtext-color)}:host([size='small']) ::slotted([slot='subtext']){font-weight:var(--row-small-size-subtext-weight) !important;font-size:var(--row-small-size-subtext-size) !important;line-height:var(--row-small-size-subtext-leading) !important;letter-spacing:var(--row-small-size-subtext-tracking) !important;}:host([size='medium']) ::slotted([slot='subtext']){font-weight:var(--row-medium-size-subtext-weight) !important;font-size:var(--row-medium-size-subtext-size) !important;line-height:var(--row-medium-size-subtext-leading) !important;letter-spacing:var(--row-medium-size-subtext-tracking) !important;}::slotted([slot='side-label']){color:var(--row-normal-state-side-text-primary-color)}@media (hover: hover){::slotted([slot='side-label']){}:host([role='option']:hover:not([disabled])) ::slotted([slot='side-label']){color:var(--row-hover-state-side-text-primary-color)}}:host([role='option'][selected]:not([disabled],.has-slotted-control)) ::slotted([slot='side-label']){color:var(--row-selected-state-side-text-primary-color)}:host([role='option']:not([disabled]):active) ::slotted([slot='side-label']){color:var(--row-pressed-state-side-text-primary-color)}::slotted([slot='side-label']) :host([role='option']:focus){color:var(--row-focus-state-side-text-primary-color)}:host([disabled]) ::slotted([slot='side-label']){color:var(--row-disabled-state-side-text-primary-color)}:host([size='small']) ::slotted([slot='side-label']){font-weight:var(--row-small-size-side-text-primary-weight);font-size:var(--row-small-size-side-text-primary-size);line-height:var(--row-small-size-side-text-primary-leading);letter-spacing:var(--row-small-size-side-text-primary-tracking)}:host([size='medium']) ::slotted([slot='side-label']){font-weight:var(--row-medium-size-side-text-primary-weight);font-size:var(--row-medium-size-side-text-primary-size);line-height:var(--row-medium-size-side-text-primary-leading);letter-spacing:var(--row-medium-size-side-text-primary-tracking)}::slotted([slot='side-subtext']){margin:0;color:var(--row-normal-state-side-text-secondary-color)}@media (hover: hover){::slotted([slot='side-subtext']){}:host([role='option']:hover:not([disabled])) ::slotted([slot='side-subtext']){color:var(--row-hover-state-side-text-secondary-color)}}:host([role='option'][selected]:not([disabled],.has-slotted-control)) ::slotted([slot='side-subtext']){color:var(--row-selected-state-side-text-secondary-color)}:host([role='option']:not([disabled]):active) ::slotted([slot='side-subtext']){color:var(--row-pressed-state-side-text-secondary-color)}::slotted([slot='side-subtext']) :host([role='option']:focus){color:var(--row-focus-state-side-text-secondary-color)}:host([disabled]) ::slotted([slot='side-subtext']){color:var(--row-disabled-state-side-text-secondary-color)}:host([size='small']) ::slotted([slot='side-subtext']){font-weight:var(--row-small-size-side-text-secondary-weight) !important;font-size:var(--row-small-size-side-text-secondary-size) !important;line-height:var(--row-small-size-side-text-secondary-leading) !important;letter-spacing:var(--row-small-size-side-text-secondary-tracking) !important;}:host([size='medium']) ::slotted([slot='side-subtext']){font-weight:var(--row-medium-size-side-text-secondary-weight) !important;font-size:var(--row-medium-size-side-text-secondary-size) !important;line-height:var(--row-medium-size-side-text-secondary-leading) !important;letter-spacing:var(--row-medium-size-side-text-secondary-tracking) !important;}::slotted([slot='leading-accessory']),::slotted([slot='trailing-accessory']){flex-shrink:0}:host([disabled]) ::slotted(.market-tooltip){pointer-events:auto !important;}::slotted([slot='control']){flex-shrink:0}:host(.preload) ::slotted([slot='control']){--row-slotted-control-transition:none}:host svg{flex-shrink:0;fill:var(--row-normal-state-drill-color)}:host([slot='displayed-selection']){pointer-events:none}:host([slot='displayed-selection']) .container{padding:0}:host([slot='displayed-selection']) .main{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host([slot='displayed-selection']) ::slotted([slot='label']){overflow:hidden;font-weight:var(--field-input-weight);font-size:inherit;line-height:inherit;text-overflow:ellipsis;white-space:nowrap}:host([slot='displayed-selection']) .side{display:none}:host([slot='displayed-selection']) ::slotted([slot='subtext']),:host([slot='displayed-selection']) ::slotted([slot='side-label']),:host([slot='displayed-selection']) ::slotted([slot='side-subtext']),:host([slot='displayed-selection']) ::slotted([slot='leading-accessory']),:host([slot='displayed-selection']) ::slotted([slot='trailing-accessory']),:host([slot='displayed-selection']) ::slotted([slot='control']){display:none}:host(.market-drag-placeholder)::before{content:'' !important;top:0;bottom:0;display:block !important;height:auto;background-color:var(--core-fill-50-color)}:host(.market-drag-placeholder) *,:host(.market-drag-placeholder) ::slotted(*){opacity:0}:host(.market-drag-clone){position:fixed;margin:0;border-radius:calc(var(--core-radius-10) * 1px);background-color:var(--core-surface-20-color);box-shadow:0 4px 8px rgba(0, 0, 0, 0.1);opacity:0.9;cursor:grabbing;transition:none}:host(.market-drag-clone)::before{display:none}:host(.market-drag-clone) .market-drag-handle{cursor:grabbing}:host(.market-drag-released){transition:transform 100ms ease}";

const MarketRow = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketRowSelected = createEvent(this, "marketRowSelected", 7);
    this.marketRowDeselected = createEvent(this, "marketRowDeselected", 7);
    this.selected = false;
    this.value = undefined;
    this.disabled = false;
    this.size = 'medium';
    this.interactive = false;
    this.transient = false;
    this.togglable = true;
    this.variant = 'regular';
    this.controlPosition = 'trailing';
    this.href = undefined;
    this.target = undefined;
    this.dragEnabled = false;
    this.dragHandlePosition = 'trailing';
    this.slottedControl = undefined;
    this.hasSideText = false;
  }
  /**
   * If a control gets slotted in, set the value to match that of the row
   */
  selectedChangeHandler() {
    if (this.slottedControl) {
      this.slottedControl.setSelection(this.selected);
    }
  }
  /**
   * If a control gets slotted in, set the value to match that of the row
   */
  disabledChangeHandler() {
    if (this.slottedControl) {
      this.slottedControl.setDisabled(this.disabled);
    }
  }
  /**
   * Used for setting the selection state to true without emiting the `marketRowSelected` event.
   */
  silentlySelect() {
    this.selected = true;
    return Promise.resolve();
  }
  /**
   * Used for setting the selection state to false without emiting the `marketRowDeselected` event.
   */
  silentlyDeselect() {
    this.selected = false;
    return Promise.resolve();
  }
  /**
   * Used for manually setting `selected` to false. Generally speaking, it
   * is preferable to avoid using this method and allow `market-row` to
   * manage its own selection state based on user interaction. It should only
   * be used for parent components that need to manage a group of rows, such as
   * `market-list`.
   */
  deselect() {
    const currentRow = {
      value: this.value,
    };
    this.selected = false;
    this.marketRowDeselected.emit(currentRow);
    return Promise.resolve();
  }
  /**
   * Used for toggling the row's selected state.
   */
  toggle() {
    this.selected = !this.selected;
    return Promise.resolve();
  }
  registerSlottedControl() {
    var _a;
    this.slottedControl = this.el.querySelector([
      getNamespacedTagFor('market-checkbox'),
      getNamespacedTagFor('market-radio'),
      getNamespacedTagFor('market-toggle'),
    ].join(','));
    if (this.slottedControl) {
      this.interactive = true;
      this.selectedChangeHandler();
      const slottedControlLabel = (_a = this.el.querySelector('[slot="label"]')) === null || _a === void 0 ? void 0 : _a.textContent;
      this.slottedControl.setAttribute('aria-label', slottedControlLabel);
    }
  }
  connectedCallback() {
    // drill variant rows should be interactive
    if (this.variant === 'drill') {
      this.interactive = true;
    }
    // link rows should not be selectable
    if (this.href) {
      this.transient = true;
    }
  }
  setControlActive(value) {
    if (this.slottedControl) {
      this.slottedControl.setActive(value);
    }
  }
  setControlHover(value) {
    if (this.slottedControl) {
      this.slottedControl.setHover(value);
    }
  }
  handleClick(e) {
    // clicks on links inside row content shouldn't select the row itself
    if (e.target.tagName === 'A' || e.target.tagName === getNamespacedTagFor('market-link').toUpperCase()) {
      return;
    }
    // We only persist selections on interactive rows that don't have the
    // `transient` property.
    if (this.interactive && !this.transient) {
      const currentRow = { value: this.value };
      if (this.togglable) {
        this.toggle();
        if (this.selected) {
          this.marketRowSelected.emit(currentRow);
        }
        else {
          this.marketRowDeselected.emit(currentRow);
        }
      }
      else {
        if (!this.selected) {
          this.selected = true;
          this.marketRowSelected.emit(currentRow);
        }
      }
    }
  }
  handleKeydown(e) {
    // don't intercept keydown of descendant elements
    // e.g. when typing into nested input fields (gross)
    if (e.target !== this.el) {
      return;
    }
    switch (e.key) {
      case 'Enter':
        this.handleClick(e);
        break;
      case ' ':
        this.handleClick(e);
        e.preventDefault(); // spacebar should not scroll page
        break;
    }
  }
  onDragStart(e) {
    const coords = e.detail;
    this.dragAndDrop = new DragAndDrop(this.el);
    this.dragAndDrop.start(coords);
  }
  onDragMove(e) {
    const coords = e.detail;
    this.dragAndDrop.move(coords);
  }
  async onDragEnd(e) {
    const coords = e.detail;
    await this.dragAndDrop.end(coords);
    this.dragAndDrop.destroy();
  }
  checkIfSideTextIsPresent() {
    const sideText = this.el.querySelector('[slot="side-label"], [slot="side-subtext"]');
    this.hasSideText = sideText ? true : false;
  }
  componentWillLoad() {
    this.checkIfSideTextIsPresent();
  }
  async componentDidUpdate() {
    // remove preload class (used to manage slotted control transitions)
    if (this.el.classList.contains('preload')) {
      await asyncRequestAnimationFrame();
      this.el.classList.remove('preload');
    }
  }
  render() {
    const { controlPosition, disabled, href, interactive, selected, slottedControl, target, variant, hasSideText, dragEnabled, dragHandlePosition, } = this;
    // we include `href` here because we want to tab directly the rendered `a` tag
    // in that case, skipping the `market-row`.
    const tabindex = interactive && !disabled && !href ? '0' : null;
    const ContainerTag = href === undefined ? 'div' : 'a';
    const ContainerTagAttrs = ContainerTag === 'a' ? { href, target } : {};
    const leadingControl = controlPosition === 'leading';
    const MarketDragHandleTagName = getNamespacedTagFor('market-drag-handle');
    return (h(Host, { role: interactive ? 'option' : 'listitem', tabindex: tabindex, "aria-selected": interactive ? Boolean(selected).toString() : null, class: classNames('market-row', 'preload', {
        'has-slotted-control': typeof slottedControl !== 'undefined',
        'has-leading-control': leadingControl,
      }), onMouseDown: () => this.setControlActive(true), onMouseUp: () => this.setControlActive(false), onMouseEnter: () => this.setControlHover(true), onMouseLeave: () => this.setControlHover(false), onClick: (e) => this.handleClick(e), onKeydown: (e) => this.handleKeydown(e), onMarketDragHandleDragStart: (e) => this.onDragStart(e), onMarketDragHandleDragMove: (e) => this.onDragMove(e), onMarketDragHandleDragEnd: (e) => this.onDragEnd(e) }, h(ContainerTag, Object.assign({ part: "container", class: "container" }, ContainerTagAttrs), dragEnabled && dragHandlePosition === 'leading' && h(MarketDragHandleTagName, null), leadingControl && h("slot", { name: "control", onSlotchange: () => this.registerSlottedControl() }), h("slot", { name: "leading-accessory" }), h("div", { class: "main", part: "main" }, h("slot", { name: "label" }), h("slot", { name: "subtext" }), h("slot", null)), h("div", { part: "side", class: classNames('side', { hidden: !hasSideText }) }, h("slot", { name: "side-label", onSlotchange: () => this.checkIfSideTextIsPresent() }), h("slot", { name: "side-subtext", onSlotchange: () => this.checkIfSideTextIsPresent() })), h("slot", { name: "trailing-accessory" }), variant === 'regular' ? (!leadingControl && h("slot", { name: "control", onSlotchange: () => this.registerSlottedControl() })) : (h("svg", { width: "16", height: "16", viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M11.7071 7.29297C12.0976 7.68349 12.0976 8.31666 11.7071 8.70718L5.70711 14.7072L4.29289 13.293L9.58579 8.00008L4.29289 2.70718L5.70711 1.29297L11.7071 7.29297Z" }))), dragEnabled && dragHandlePosition === 'trailing' && h(MarketDragHandleTagName, null))));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "selected": ["selectedChangeHandler"],
    "disabled": ["disabledChangeHandler"]
  }; }
};
MarketRow.style = marketRowCss;

export { MarketRow as market_row };
